import React from "react";
import { AlertCircle, Image, Loader2, Trash2 } from "lucide-react";
import { useDeleteMediaMutation, useMediaQuery } from "../../api/mediaApi.ts";
import { useAuth } from "react-oidc-context";
import { format } from "date-fns";
import { useLanguageStore } from "../../stores/languageStore.ts";
import { toast } from "react-hot-toast";
import { t } from "@lingui/core/macro";
import ConfirmActionButton from "../ConfirmActionButton.tsx";
import { Trans } from "@lingui/react/macro";

interface MediaWallProps {
  userId?: string;
}

const MediaWall: React.FC<MediaWallProps> = ({ userId }) => {
  const { user, isAuthenticated, signinRedirect } = useAuth();
  const { data, isLoading, isError, error, refetch } = useMediaQuery(userId, {
    enabled: !!userId && isAuthenticated,
  });
  const { mutate, isPending } = useDeleteMediaMutation();
  const { dateFnsLocale } = useLanguageStore();

  const isOwnProfile = userId === user?.profile.sub;

  const handleDelete = (mediaId: string) => {
    mutate(mediaId, {
      onSuccess: async () => await refetch(),
      onError: () =>
        toast.error(t`Could not delete image. Please try again later.`),
    });
  };

  const formatDate = (dateString: string) => {
    return format(dateString, "PPP", { locale: dateFnsLocale });
  };

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="max-w-md text-center">
          <Image className="mx-auto mb-4 h-16 w-16 text-gray-300" />
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            <Trans>Seems like you are not logged in.</Trans>
          </h3>
          <p className="text-gray-600">
            <Trans>Login or create an account to see more!</Trans>
          </p>
          <button
            className="bg-soluud-primary hover:bg-soluud-primary-hover my-4 w-full cursor-pointer rounded-xl px-4 py-2 text-white transition"
            onClick={() =>
              signinRedirect({ redirect_uri: window.location.href }).catch(
                console.error,
              )
            }
          >
            <Trans>Login</Trans>
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <Loader2 className="text-soluud-primary mx-auto mb-4 h-12 w-12 animate-spin" />
          <p className="text-soluud-text-secondary">Loading media...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="max-w-md text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            Failed to load media
          </h3>
          <p className="text-gray-600">
            {error instanceof Error
              ? error.message
              : "An error occurred while loading the media wall"}
          </p>
        </div>
      </div>
    );
  }

  if (!data?.media || data.media.length === 0) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="max-w-md text-center">
          <Image className="mx-auto mb-4 h-16 w-16 text-gray-300" />
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            <Trans>No media yet</Trans>
          </h3>
          <p className="text-gray-600">
            <Trans>This looks empty!</Trans>
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto w-full">
        <div className={"flex flex-col"}>
          <div className="space-y-6">
            {data.media.map((item, index) => (
              <div key={item.id || index}>
                <div className="relative overflow-hidden rounded-lg bg-gray-100 shadow-md">
                  <div className="relative">
                    <img
                      src={item.uri}
                      alt={item.description}
                      className="h-auto w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex justify-between bg-white p-4">
                    {item.description && (
                      <p className="mb-2 text-sm text-gray-700">
                        {item.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4">
                      <div className="ml-4 h-full border-l border-gray-300"></div>
                      <div className="flex flex-col items-end">
                        <p className="text-xs">{formatDate(item.createdAt)}</p>
                        {isOwnProfile && (
                          <ConfirmActionButton
                            onConfirm={() => handleDelete(item.id)}
                            isLoading={isPending}
                            confirmText={t`Delete`}
                            cancelText={t`Cancel`}
                            className="text-soluud-warning mt-2 cursor-pointer border hover:bg-red-50"
                            cancelClassName="text-gray-600 hover:bg-gray-100 border mt-2"
                            confirmClassName="text-red-600 hover:bg-red-50 border mt-2"
                          >
                            <Trash2 className="h-3 w-3" />
                            <Trans>Delete</Trans>
                          </ConfirmActionButton>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default MediaWall;
